import type { JSONContent } from '@tiptap/react';

import { useI18nContext } from '@/i18n/i18n-react';
import { getBannerPlainText } from '@/lib/app-editor/coupon-banner-form';
import type { CouponPosition } from '@/lib/app-editor/coupon-banner-form';
import { cn } from '@/lib/utils';

export interface CouponBannerPreviewProps {
  bannerText: JSONContent;
  backgroundColorHex: string;
  foregroundColorHex: string;
  animated: boolean;
  /** Where the store places the banner; observable as `data-position`. */
  position: CouponPosition;
  /** Tap on the banner (the editor goes to the text tab). */
  onPress?: () => void;
}

const MIN_STRIP_SECONDS = 5;
const SECONDS_PER_CHARACTER = 0.25;
const STRIP_REPEATS = 14;

function renderContent(nodes: JSONContent[] | undefined): React.ReactNode[] {
  return (nodes ?? []).map((node, index) => {
    if (node.text === undefined) {
      return <span key={index}>{renderContent(node.content)}</span>;
    }
    return node.marks?.some((mark) => mark.type === 'bold') ? (
      <strong key={index}>{node.text}</strong>
    ) : (
      node.text
    );
  });
}

function BannerText({
  bannerText,
  className,
}: {
  bannerText: JSONContent;
  className?: string;
}) {
  return (
    <span className={cn('shrink-0 whitespace-pre', className)}>
      {renderContent(bannerText.content)}
    </span>
  );
}

/** The coupon banner as the app draws it: a strip with the text and "COPIAR". */
export function CouponBannerPreview({
  bannerText,
  backgroundColorHex,
  foregroundColorHex,
  animated,
  position,
  onPress,
}: CouponBannerPreviewProps) {
  const { LL } = useI18nContext();
  const duration = Math.max(
    MIN_STRIP_SECONDS,
    getBannerPlainText(bannerText).length * SECONDS_PER_CHARACTER
  );

  const content = (
    <>
      <span
        data-banner-text
        className="flex min-w-0 flex-1 overflow-hidden whitespace-nowrap text-ellipsis"
      >
        {animated ? (
          <span
            data-banner-strip
            className="flex h-full animate-[scrolling-text_linear_infinite] will-change-transform motion-reduce:animate-none"
            style={{ animationDuration: `${duration}s` }}
          >
            {Array.from({ length: STRIP_REPEATS }, (_, index) => (
              <BannerText
                key={index}
                bannerText={bannerText}
                className={index < STRIP_REPEATS - 1 ? 'pr-2' : undefined}
              />
            ))}
          </span>
        ) : (
          <BannerText bannerText={bannerText} />
        )}
      </span>
      <span
        style={{
          backgroundColor: foregroundColorHex,
          color: backgroundColorHex,
        }}
        className="shrink-0 rounded-full px-2 py-0.5 text-[8px] font-bold"
      >
        COPIAR
      </span>
    </>
  );
  const shared = {
    'data-coupon-banner': true,
    'data-position': position,
    'data-animated': animated,
    style: { backgroundColor: backgroundColorHex, color: foregroundColorHex },
    className:
      'flex min-h-7 w-full items-center gap-2 px-2 py-1 text-left text-[10px]',
  };

  // Without `onPress` (a thumbnail inside another button) it is not a control.
  if (!onPress) return <div {...shared}>{content}</div>;
  return (
    <button
      type="button"
      {...shared}
      aria-label={LL.couponEditor.canvas.editText()}
      onClick={onPress}
      className={cn(
        shared.className,
        'outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
      )}
    >
      {content}
    </button>
  );
}
